import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Navigate, useNavigate, useSearchParams } from 'react-router-dom';
import { Wrench, ClipboardCheck, Repeat, ArrowRight, Phone, MapPin, Clock, ShieldCheck, CheckCircle2, Building2 } from 'lucide-react';
import { useEffect, useRef } from 'react';

const LOGO_URL = 'https://static.prod-images.emergentagent.com/jobs/b0feedf7-36df-41e9-9474-99c5d4f77205/images/70da477a4272b0edc6ede2f5e422bf85f189422131684b511c96d6b04c9062b9.jpeg';

const SERVICES = [
  {
    key: 'una_tantum',
    icon: Wrench,
    title: 'Intervento a Chiamata',
    desc: 'Hai un problema specifico? Richiedi un intervento mirato per riparare, sostituire o sistemare i tuoi serramenti.',
    details: ['Costo chiamata EUR 25', 'Manodopera EUR 40/h (min. 1 ora)', 'Rimborso kilometrico in base alla sede'],
    cta: 'Prenota il tuo Intervento',
  },
  {
    key: 'check_generale',
    icon: ClipboardCheck,
    title: 'Check Generale',
    desc: 'Il nostro servizio principale. Controlliamo, registriamo, sistemiamo e oliamo tutti i tuoi serramenti.',
    details: ['Durata circa 1h 30min', 'A partire da 100 EUR', 'Preventivo dedicato se necessario'],
    cta: 'Prenota il Check',
    recommended: true,
  },
  {
    key: 'abbonamento',
    icon: Repeat,
    title: 'Abbonamento',
    desc: 'Manutenzione programmata 1 o 2 volte all\'anno per una cura continua dei tuoi serramenti.',
    details: ['Priorità prenotazione', 'Sconti su ricambi', 'Piano personalizzato'],
    cta: 'Richiedi Info',
  },
];

const SERVICES_LIST = [
  'Sostituzione vetri',
  'Riparazione cerniere e maniglie',
  'Sostituzione guarnizioni',
  'Regolazione chiusure',
  'Isolamento termico e acustico',
  'Riparazione tapparelle e persiane',
  'Riparazione e sostituzione zanzariere',
];

const GoogleIcon = () => (
  <svg className="w-5 h-5" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" />
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" />
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" />
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" />
  </svg>
);

export default function LandingPage() {
  const { user, login } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const servicesRef = useRef(null);

  // Read QR code params and store fonte
  useEffect(() => {
    const fonte = searchParams.get('fonte');
    const servizio = searchParams.get('servizio');
    if (fonte) {
      localStorage.setItem('regola_fonte', fonte);
    }
    if (servizio) {
      localStorage.setItem('regola_servizio', servizio);
      // Auto-scroll to services section
      setTimeout(() => {
        const el = document.getElementById('servizi');
        if (el) el.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  }, [searchParams]);

  const handleServiceLogin = (serviceKey) => {
    if (serviceKey === 'check_generale') {
      navigate('/check-info');
    } else if (serviceKey === 'una_tantum') {
      navigate('/intervento-info');
    } else if (serviceKey === 'abbonamento') {
      navigate('/abbonamento');
    } else {
      navigate('/dashboard');
    }
  };

  if (user) return <Navigate to="/dashboard" replace />;

  return (
    <div className="min-h-screen bg-white" style={{ fontFamily: "'Plus Jakarta Sans', sans-serif" }} data-testid="landing-page">

      {/* ── NAV ── */}
      <nav className="sticky top-0 z-50 bg-white border-b border-slate-100" data-testid="navbar">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 h-20 flex items-center justify-between">
          <img src={LOGO_URL} alt="REGOLA" className="h-48" data-testid="brand-logo" />
          <div className="flex items-center gap-6">
            <a href="#servizi" className="hidden md:block text-sm text-slate-500 hover:text-slate-900 transition-colors font-medium">
              Servizi
            </a>
            <a href="#contatti" className="hidden md:block text-sm text-slate-500 hover:text-slate-900 transition-colors font-medium">
              Contatti
            </a>
            <Button
              data-testid="nav-login-btn"
              onClick={login}
              variant="outline"
              className="border-slate-200 text-slate-600 hover:bg-slate-50 rounded-full px-5 text-sm font-semibold gap-2 transition-all hover:-translate-y-0.5"
            >
              Admin
            </Button>
          </div>
        </div>
      </nav>

      {/* ── HERO ── */}
      <section className="py-16 md:py-24 lg:py-28">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-blue-50 border border-blue-100 mb-8">
                <div className="w-1.5 h-1.5 rounded-full bg-blue-500" />
                <span className="text-blue-600 text-xs font-semibold tracking-wide uppercase">Manutenzione serramenti</span>
              </div>

              <h1
                className="text-4xl sm:text-5xl lg:text-[3.5rem] font-extrabold text-slate-900 leading-[1.1] tracking-tight mb-6"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                I tuoi serramenti,{' '}
                <span className="text-blue-500">la nostra cura.</span>
              </h1>

              <p className="text-slate-500 text-base md:text-lg leading-relaxed mb-10 max-w-lg">
                Manutenzione professionale di finestre e porte.
                Preventivo istantaneo, assistenza dedicata e intervento rapido.
              </p>

              <div className="flex flex-col sm:flex-row gap-3">
                <Button
                  data-testid="google-login-btn"
                  onClick={() => { const el = document.getElementById('servizi'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
                  size="lg"
                  className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-8 py-6 text-base font-semibold gap-2 transition-all hover:-translate-y-0.5"
                >
                  Inizia Ora
                  <ArrowRight className="w-4 h-4" />
                </Button>
                <a href="#servizi">
                  <Button
                    variant="outline"
                    size="lg"
                    className="rounded-full px-8 py-6 text-base border-slate-200 text-slate-700 hover:bg-slate-50"
                    data-testid="learn-more-btn"
                  >
                    Scopri i servizi
                  </Button>
                </a>
              </div>

              {/* Stats */}
              <div className="flex gap-10 mt-14 pt-8 border-t border-slate-100">
                {[
                  { n: '40+', label: 'Anni di esperienza' },
                  { n: '3', label: 'Servizi dedicati' },
                ].map((s, i) => (
                  <div key={i}>
                    <p className="text-3xl font-extrabold text-blue-500" style={{ fontFamily: "'Manrope', sans-serif" }}>{s.n}</p>
                    <p className="text-xs text-slate-400 mt-1 font-medium">{s.label}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Image */}
            <div className="relative">
              <div className="rounded-2xl overflow-hidden shadow-sm border border-slate-100">
                <img
                  src="https://images.pexels.com/photos/7482634/pexels-photo-7482634.jpeg?auto=compress&cs=tinysrgb&w=800"
                  alt="Carpentiere che leviga il legno con cura"
                  className="w-full h-[400px] lg:h-[480px] object-cover"
                />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── SERVICES ── */}
      <section id="servizi" className="py-20 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="mb-14">
            <p className="text-blue-500 text-xs font-bold uppercase tracking-[0.2em] mb-3">I nostri servizi</p>
            <h2
              className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              3 modi per prenderti cura<br className="hidden md:block" /> dei tuoi serramenti
            </h2>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5 items-end" ref={servicesRef}>
            {SERVICES.map((s, i) => {
              const isHighlighted = searchParams.get('servizio') === s.key;
              return (
              <Card
                key={i}
                className={`relative border rounded-xl overflow-hidden transition-all hover:shadow-md hover:-translate-y-0.5 ${
                  isHighlighted ? 'border-blue-500 ring-2 ring-blue-300 bg-blue-50/30 md:scale-[1.04] md:shadow-lg md:z-10' :
                  s.recommended ? 'border-blue-300 bg-white md:scale-[1.04] md:shadow-lg md:z-10' : 'border-slate-200 bg-white'
                }`}
                data-testid={`service-card-${i}`}
                id={`service-${s.key}`}
              >
                {s.recommended && !isHighlighted && (
                  <div className="absolute top-4 right-4 bg-blue-500 text-white text-[11px] font-bold px-3 py-1 rounded-full" data-testid="recommended-badge">
                    Consigliato
                  </div>
                )}
                {isHighlighted && (
                  <div className="absolute top-4 right-4 bg-green-500 text-white text-[11px] font-bold px-3 py-1 rounded-full animate-pulse">
                    Selezionato
                  </div>
                )}
                <CardContent className={s.recommended || isHighlighted ? 'p-7' : 'p-6'}>
                  <div className="w-12 h-12 rounded-xl bg-blue-500 flex items-center justify-center mb-5">
                    <s.icon className="w-5 h-5 text-white" />
                  </div>
                  <h3 className="text-base font-bold text-slate-900 mb-2" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    {s.title}
                  </h3>
                  <p className="text-sm text-slate-500 leading-relaxed mb-5">{s.desc}</p>
                  <ul className="space-y-2.5 mb-6">
                    {s.details.map((d, j) => (
                      <li key={j} className="flex items-start gap-2 text-sm text-slate-600">
                        <CheckCircle2 className="w-4 h-4 text-blue-400 flex-shrink-0 mt-0.5" />
                        {d}
                      </li>
                    ))}
                  </ul>
                  <Button
                    onClick={() => handleServiceLogin(s.key)}
                    className={`w-full rounded-full font-semibold gap-2 transition-all hover:-translate-y-0.5 ${
                      isHighlighted ? 'bg-green-500 hover:bg-green-600 text-white' :
                      s.recommended
                        ? 'bg-blue-500 hover:bg-blue-600 text-white'
                        : 'bg-blue-400 hover:bg-blue-500 text-white'
                    }`}
                    data-testid={`service-cta-${i}`}
                  >
                    {s.cta} <ArrowRight className="w-4 h-4" />
                  </Button>
                </CardContent>
              </Card>
            )})}
          </div>

          {/* Card Aziende / Hotel / B&B */}
          <Card className="mt-8 border border-blue-200 bg-gradient-to-r from-slate-900 to-slate-800 rounded-xl overflow-hidden" data-testid="business-card">
            <CardContent className="p-8 md:p-10 flex flex-col md:flex-row items-center gap-6 md:gap-10">
              <div className="w-16 h-16 rounded-2xl bg-blue-500 flex items-center justify-center flex-shrink-0">
                <Building2 className="w-7 h-7 text-white" />
              </div>
              <div className="flex-1 text-center md:text-left">
                <h3
                  className="text-lg md:text-xl font-extrabold text-white mb-3"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                >
                  Hai un'azienda, un hotel, un B&B?
                </h3>
                <p className="text-sm text-slate-300 leading-relaxed max-w-2xl">
                  Non hai nessuno che ti garantisce il corretto funzionamento di porte e finestre per tutto l'anno?
                  Contattaci e svilupperemo insieme la tua manutenzione su misura.
                </p>
              </div>
              <Button
                onClick={() => navigate('/aziende')}
                className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-8 py-5 font-semibold gap-2 flex-shrink-0 transition-all hover:-translate-y-0.5"
                data-testid="business-cta-btn"
              >
                Contattaci <ArrowRight className="w-4 h-4" />
              </Button>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* ── WHAT WE DO ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-14 items-center">
            <div className="rounded-2xl overflow-hidden border border-slate-100">
              <img
                src="https://images.pexels.com/photos/5691501/pexels-photo-5691501.jpeg?auto=compress&cs=tinysrgb&w=800"
                alt="Tecnico al lavoro"
                className="w-full h-[380px] object-cover"
              />
            </div>
            <div>
              <p className="text-blue-500 text-xs font-bold uppercase tracking-[0.2em] mb-3">Cosa facciamo</p>
              <h2
                className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-8"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Manutenzione completa<br /> per ogni esigenza
              </h2>
              <ul className="space-y-4">
                {SERVICES_LIST.map((s, i) => (
                  <li key={i} className="flex items-center gap-3 text-sm text-slate-600 font-medium">
                    <div className="w-7 h-7 rounded-full bg-blue-50 flex items-center justify-center flex-shrink-0">
                      <ShieldCheck className="w-3.5 h-3.5 text-blue-500" />
                    </div>
                    {s}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* ── CHI SIAMO ── */}
      <section className="py-20 bg-slate-50/50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <div>
              <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3">La nostra storia</p>
              <h2
                className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight mb-5"
                style={{ fontFamily: "'Manrope', sans-serif" }}
              >
                Dal 1984, serramenti<br />di qualità in famiglia.
              </h2>
              <div className="space-y-4 text-sm text-slate-600 leading-relaxed">
                <p>
                  <strong>Rivas 1984</strong> è un'azienda familiare fondata oltre 40 anni fa e guidata oggi da
                  <strong> Valentina</strong> e <strong>Riccardo Sartori</strong>. Da sempre ci occupiamo di serramenti su misura
                  con il nostro metodo <em>FinestreinPosa</em>: progettazione 3D, posa qualificata e garanzia totale 10+10 anni.
                </p>
                <p>
                  <strong>REGOLA</strong> è il nostro marchio dedicato alla <strong>manutenzione e assistenza</strong> di porte e finestre.
                  Nasce dall'ascolto dei nostri clienti, che dopo l'installazione ci chiedevano un servizio continuativo
                  per mantenere i serramenti sempre efficienti e in perfette condizioni.
                </p>
                <p>
                  Con REGOLA portiamo la stessa cura artigianale e organizzazione aziendale
                  che ci contraddistingue da 40 anni, direttamente a casa tua.
                </p>
              </div>
              <div className="flex gap-6 mt-6">
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-blue-600">40+</p>
                  <p className="text-[11px] text-slate-500">Anni di esperienza</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-blue-600">10+10</p>
                  <p className="text-[11px] text-slate-500">Anni di garanzia</p>
                </div>
                <div className="text-center">
                  <p className="text-2xl font-extrabold text-blue-600">100%</p>
                  <p className="text-[11px] text-slate-500">Azienda familiare</p>
                </div>
              </div>
            </div>
            <div className="bg-white rounded-2xl shadow-sm border border-slate-100 p-8 space-y-5">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <ShieldCheck className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900 mb-1">Mentalità artigiana, organizzazione aziendale</p>
                  <p className="text-xs text-slate-500">Processo chiaro, qualità e assistenza: la cura artigiana si unisce a una regia organizzativa solida.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <CheckCircle2 className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900 mb-1">Certezza del risultato</p>
                  <p className="text-xs text-slate-500">Nessun ripensamento, nessuna sorpresa. Controllo totale su ogni intervento di manutenzione.</p>
                </div>
              </div>
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Clock className="w-5 h-5 text-blue-600" />
                </div>
                <div>
                  <p className="text-sm font-bold text-slate-900 mb-1">Supporto che non ti abbandona</p>
                  <p className="text-xs text-slate-500">Il rapporto non finisce con i lavori: restiamo il tuo punto di riferimento con assistenza continua.</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── RECENSIONI ── */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-12">
            <p className="text-xs font-bold text-blue-600 uppercase tracking-widest mb-3">Cosa dicono di noi</p>
            <h2
              className="text-2xl md:text-3xl font-extrabold text-slate-900 tracking-tight"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              La fiducia dei nostri clienti
            </h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {[
              {
                quote: 'La tranquillità che tutto verrà svolto nelle modalità ed entro i termini concordati è assicurata con Rivas. CON RIVAS... NO STRESS!',
                name: 'Aurora Albertini',
                location: 'Pozzolengo — BS',
              },
              {
                quote: 'Hanno effettuato egregiamente il lavoro in maniera organizzata, pulita e senza imprevisti. Portandosi via tutto il materiale e lasciando il cantiere il più pulito possibile.',
                name: 'Danilo Ferrara',
                location: 'Monticelli Brusati — BS',
              },
              {
                quote: 'Valentina darà vita alle vostre idee, realizzando il progetto in 3D... dopodiché Riccardo si occuperà della realizzazione con cura e maestria.',
                name: 'Damiano Pezzini',
                location: 'Casaloldo — MN',
              },
            ].map((r, i) => (
              <Card key={i} className="border-0 shadow-sm rounded-2xl" data-testid={`review-${i}`}>
                <CardContent className="p-6">
                  <div className="flex gap-1 mb-4">
                    {[...Array(5)].map((_, j) => (
                      <svg key={j} className="w-4 h-4 text-amber-400 fill-current" viewBox="0 0 20 20">
                        <path d="M9.049 2.927c.3-.921 1.603-.921 1.902 0l1.07 3.292a1 1 0 00.95.69h3.462c.969 0 1.371 1.24.588 1.81l-2.8 2.034a1 1 0 00-.364 1.118l1.07 3.292c.3.921-.755 1.688-1.54 1.118l-2.8-2.034a1 1 0 00-1.175 0l-2.8 2.034c-.784.57-1.838-.197-1.539-1.118l1.07-3.292a1 1 0 00-.364-1.118L2.98 8.72c-.783-.57-.38-1.81.588-1.81h3.461a1 1 0 00.951-.69l1.07-3.292z" />
                      </svg>
                    ))}
                  </div>
                  <p className="text-sm text-slate-600 italic leading-relaxed mb-4">"{r.quote}"</p>
                  <div className="flex items-center gap-3">
                    <div className="w-9 h-9 bg-blue-100 rounded-full flex items-center justify-center">
                      <span className="text-xs font-bold text-blue-700">{r.name.split(' ').map(n => n[0]).join('')}</span>
                    </div>
                    <div>
                      <p className="text-sm font-bold text-slate-900">{r.name}</p>
                      <p className="text-xs text-slate-500">{r.location}</p>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 bg-slate-900">
        <div className="max-w-3xl mx-auto px-6 lg:px-8 text-center">
          <h2
            className="text-2xl md:text-3xl font-extrabold text-white tracking-tight mb-4"
            style={{ fontFamily: "'Manrope', sans-serif" }}
          >
            Pronto a prenderti cura<br /> dei tuoi serramenti?
          </h2>
          <p className="text-slate-400 mb-8 max-w-md mx-auto text-sm leading-relaxed">
            Scegli il servizio più adatto a te.
            Nessun impegno, totale trasparenza sui costi.
          </p>
          <Button
            onClick={() => { const el = document.getElementById('servizi'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }}
            size="lg"
            className="bg-blue-500 hover:bg-blue-600 text-white rounded-full px-10 py-6 text-base font-semibold gap-2 transition-all hover:-translate-y-0.5"
            data-testid="cta-bottom-btn"
          >
            Scopri i Servizi
            <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer id="contatti" className="bg-slate-50 border-t border-slate-100 py-12">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <img src={LOGO_URL} alt="REGOLA" className="h-14 mb-4" />
              <p className="text-sm text-slate-500 leading-relaxed">
                Manutenzione professionale di serramenti dal 1984.
                Qualità, affidabilità e trasparenza.
              </p>
            </div>
            <div>
              <p className="text-slate-900 font-semibold text-sm mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>Contatti</p>
              <div className="space-y-3 text-sm text-slate-500">
                <div className="flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-blue-500" />
                  <span>Via Donatori di Sangue 2/d, 46043 Castiglione delle Stiviere (MN)</span>
                </div>
                <div className="flex items-center gap-2">
                  <Phone className="w-4 h-4 text-blue-500" />
                  <span>info@assistenzaregola.it</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-blue-500" />
                  <span>Lun-Ven 08:00-18:00</span>
                </div>
              </div>
            </div>
            <div>
              <p className="text-slate-900 font-semibold text-sm mb-4" style={{ fontFamily: "'Manrope', sans-serif" }}>Area di Servizio</p>
              <p className="text-sm text-slate-500 leading-relaxed">
                Brescia, Cremona, Verona, Mantova e Lago di Garda.
              </p>
            </div>
          </div>
          <div className="border-t border-slate-200 mt-10 pt-6 text-center text-xs text-slate-400">
            REGOLA by Rivas - Tutti i diritti riservati
          </div>
        </div>
      </footer>
    </div>
  );
}
